import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";

export default function AdminSetup() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [needed, setNeeded] = useState(false);
  const [f, setF] = useState({ email: "", name: "", password: "", confirm: "" });
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/bootstrap-status");
        setNeeded(!!data.bootstrap_needed);
      } catch (e) {
        setErr(formatApiError(e));
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  if (checking) return <div className="min-h-screen flex items-center justify-center text-ink-500">Verifica configurazione…</div>;
  if (!needed) return <Navigate to={user ? "/admin" : "/admin/login"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (f.password !== f.confirm) { setErr("Le due password non coincidono"); return; }
    if (f.password.length < 8) { setErr("La password deve avere almeno 8 caratteri"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/bootstrap-superadmin", {
        email: f.email.trim(),
        name: f.name.trim(),
        password: f.password,
      });
      if (data?.access_token) localStorage.setItem("sb_token", data.access_token);
      // Full reload so AuthProvider picks up the new session cleanly
      window.location.href = "/admin";
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-sand-50">
      <form onSubmit={submit} className="card-elevated p-8 w-full max-w-lg" data-testid="admin-setup-form">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-8 h-8 text-bronze-600" strokeWidth={1.5} />
          <div>
            <div className="eyebrow">Prima configurazione</div>
            <h1 className="text-3xl">Crea account Superadmin</h1>
          </div>
        </div>
        <p className="mt-4 text-sm text-ink-500">
          Nessun Superadmin risulta configurato. Crea ora l'account proprietario del sistema.
          Questa procedura verrà disattivata definitivamente al primo utilizzo.
        </p>
        <div className="mt-6 space-y-4">
          <div>
            <Label htmlFor="name">Nome visualizzato</Label>
            <Input id="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                   data-testid="setup-name" autoFocus required />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                   data-testid="setup-email" required />
          </div>
          <div>
            <Label htmlFor="pwd">Password</Label>
            <Input id="pwd" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })}
                   data-testid="setup-password" required />
            <p className="mt-1 text-xs text-ink-500">Almeno 8 caratteri, una lettera e un numero.</p>
          </div>
          <div>
            <Label htmlFor="pwd2">Conferma password</Label>
            <Input id="pwd2" type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })}
                   data-testid="setup-confirm" required />
          </div>
          {err && <div className="text-sm text-red-700" data-testid="setup-error">{err}</div>}
          <Button type="submit" className="w-full rounded-full" data-testid="setup-submit" disabled={loading}>
            {loading ? "Creazione…" : "Crea Superadmin e accedi"}
          </Button>
        </div>
      </form>
    </div>
  );
}
